import type { EntityState, Slice } from "@reduxjs/toolkit";
import { createEntityAdapter, createSlice, isAnyOf } from "@reduxjs/toolkit";
import type {
	GetDefaultOrganizationSettingsRequest,
	GetDefaultOrganizationSettingsResponse,
	UpdateDefaultOrganizationSettingsResponse,
} from "@somewear/api/src/proto/api/organization_pb";
import type { OrganizationDefaultSettings } from "@somewear/api/src/proto/organization_default_settings_proto_pb";
import { createActionSet } from "@somewear/model";
import type { WritableDraft } from "immer/dist/types/types-external";

import type { OrganizationWorkspaceDefaultsRequestSchema } from "./organization.model";
import type { OrganizationState } from "./organization.state";

export interface IOrganizationWorkspaceSettingsState extends OrganizationDefaultSettings.AsObject {
	organizationId: string;
}

export const organizationDeviceSettingsActions = {
	updateDefaultDeviceSettings: createActionSet<
		OrganizationWorkspaceDefaultsRequestSchema,
		UpdateDefaultOrganizationSettingsResponse.AsObject
	>("organization/defaultSettings/update"),
	getDefaultDeviceSettings: createActionSet<
		GetDefaultOrganizationSettingsRequest.AsObject,
		GetDefaultOrganizationSettingsResponse.AsObject
	>("organization/defaultSettings/get"),
};

const adapter = createEntityAdapter<IOrganizationWorkspaceSettingsState, string>({
	selectId: (org) => org.organizationId,
});

export const {
	selectAll: selectAllOrganizationDeviceSettings,
	selectById: selectOrganizationDeviceSettingsById,
} = adapter.getSelectors((state: OrganizationState) => state.organizationDeviceSettings);

function updateSettings(
	state: WritableDraft<EntityState<IOrganizationWorkspaceSettingsState, string>>,
	payload: GetDefaultOrganizationSettingsResponse.AsObject,
): IOrganizationWorkspaceSettingsState | undefined {
	if (!payload.organizationId || !payload.settings || !organizationHasSettings(payload)) return;

	adapter.upsertOne(state, {
		organizationId: payload.organizationId,
		...payload.settings,
	});
}

function organizationHasSettings(
	response: GetDefaultOrganizationSettingsResponse.AsObject,
): boolean {
	return Boolean(response.settings?.sentTimestamp && response.settings.sentTimestamp > 0);
}

export const organizationDefaultWorkspaceSettingsSlice: Slice = createSlice({
	name: "organizationDefaultWorkspaceSettings",
	initialState: adapter.getInitialState({
		isLoading: false,
	}),
	reducers: {
		_: () => {},
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			isAnyOf(
				organizationDeviceSettingsActions.getDefaultDeviceSettings.pending,
				organizationDeviceSettingsActions.updateDefaultDeviceSettings.pending,
			),
			(state) => {
				state.isLoading = true;
			},
		);
		builder.addMatcher(
			isAnyOf(
				organizationDeviceSettingsActions.getDefaultDeviceSettings.fulfilled,
				organizationDeviceSettingsActions.updateDefaultDeviceSettings.fulfilled,
			),
			(state, action) => {
				state.isLoading = false;
				updateSettings(state, action.payload.data);
			},
		);
		builder.addMatcher(
			isAnyOf(
				organizationDeviceSettingsActions.getDefaultDeviceSettings.rejected,
				organizationDeviceSettingsActions.updateDefaultDeviceSettings.rejected,
			),
			(state) => {
				state.isLoading = false;
			},
		);
	},
});
